import { createApi } from '@reduxjs/toolkit/query/react'
import { IChatboxChat, IChatboxResponse, ITopics } from '../../interfaces/chats/IChatResponse'
import { deviceInfoAxiosQuery } from '../../utils/axios/deviceInfo.axios'
import { ICards } from '../../interfaces/cards/ICards'
import { IBrands } from '../../interfaces/brands/IBrands'
import { IPage } from '../../interfaces/models/models/paginated-response.model'

export const chatboxApi = createApi({
    reducerPath: 'chatbox',
    baseQuery: deviceInfoAxiosQuery(),
	tagTypes: ['brand', 'topics', 'chats', 'updated-news', 'agent-kb'],
    endpoints: (builder) => ({
		getBrandingConfig: builder.query<IBrands, string>({
			query: (hotel) => ({
				url: `/branding-configs/${hotel}/SOFIA`,
				method: 'GET',
			}),
			providesTags: ['brand']
		}),
		uploadChatFile: builder.mutation<{name: string, url: string}[], FormData>({
			query: (data) => ({
				url: `/files/upload-chat-files`,
				method: 'POST',
				data
			}),
		}),
		getCards: builder.query<ICards[], void>({
			query: () => ({
				url: `/cards`,
				method: 'GET',
			}),
			providesTags: ['updated-news']
		}),
		chatbox: builder.mutation<{data: IChatboxChat}, {
			message: string, 
			files?: {name: string, url: string}[], 
			topic?: string, 
			user: {name: string, email: string, id: string}
			clarify?: boolean
		}>({
			query: (payload) => ({
				url: `/chatbox/chat`,
				method: 'POST',
				data: payload,
			}),
		}),
		chatboxFile: builder.mutation<{data: IChatboxChat}, FormData>({
			query: (payload) => ({
				url: `/chatbox/chat`,
				method: 'POST',
				data: payload,
				// headers: {
				// 'Access-Control-Allow-Origin': '*',
				// },
			}),
		}),
		topicsList: builder.query<{elements: ITopics[], page: IPage}, {type: 'HISTORY' | 'TOPIC' | null, userId: string, page: number, limit: number}>({
			query: ({type, userId, page, limit}) => ({
				url: type ? `/chatbox/topics?type=${type}&userId=${userId}&page=${page}&limit=${limit}` : `/chatbox/topics?userId=${userId}&page=${page}&limit=${limit}`,
				method: 'GET'
			}),
			providesTags: ['topics']
		}),
		getTopicChats: builder.mutation<{data: IChatboxResponse}, {topic: string, userId: string}>({
			query: ({topic, userId}) => ({
				url: `/chatbox/chat?topic=${topic}&userId=${userId}`,
				method: 'GET'
			})
		}),
		saveTopic: builder.mutation<{data: IChatboxResponse}, string>({
			query: (topic) => ({
				url: `/chatbox/chat/${topic}/topic`,
				method: 'PUT'
			}),
			invalidatesTags: ['topics']
		}),
		reactMessage: builder.mutation<{data: IChatboxResponse}, {action: 'like' | 'dislike' | 'favorite', messageId: string}>({
			query: ({action, messageId}) => ({
				url: `/chatbox/messages/${messageId}/reaction`,
				method: 'PUT',
				data: {action}
			}),
		}),
		reportIssue: builder.mutation<{data: IChatboxResponse}, { messageId: string,  userIssue: string,  topicId: string}>({
			query: ({ messageId, userIssue, topicId}) => ({
				url: `/chatbox/chats/issues/report`,
				method: 'PUT',
				data: {messageId, userIssue, topicId}
			}),
		}),
		    getAgentKbUrlMap: builder.query<Record<string, string>, string>({
            // pass the `sub` string directly to the query
            query: (sub) => ({
                url: `/agent-knowledge-base-files/url-map?sub=${encodeURIComponent(sub)}`,
                method: 'GET',
            }),
            providesTags: ['agent-kb'],
        }),
    }),
})

export const {
	useGetBrandingConfigQuery,
	useGetCardsQuery,
	useUploadChatFileMutation,
	useChatboxMutation,
	useChatboxFileMutation,
	useTopicsListQuery,
	useGetTopicChatsMutation,
	useSaveTopicMutation,
	useReactMessageMutation,
	useReportIssueMutation,
	useGetAgentKbUrlMapQuery
} = chatboxApi